import * as Sentry from '@sentry/react'
import { appInfo } from '../appInfo'
import { describeNonError } from './describeNonError'

type FrontendErrorContext = {
  extra?: Record<string, unknown>
  tags?: Record<string, string>
  /** for bare strings only; errors are always captured at their own level */
  level?: Sentry.SeverityLevel
}

/**
 * Frontend equivalent of regional-api's errorLogger. Routes Sentry capture through a single
 * surface so route/build/viewport context, tag conventions, and Error vs. message dispatch
 * are consistent across the app. Mirrors the `(error, context)` shape of the backend logger.
 *
 * Bare strings go through `captureMessage` (not `captureException`) so custom error classes
 * elsewhere don't get flattened to a generic `Error` fingerprint — see W2 lost-class-info note
 * in sentry-prd.md.
 */
export const captureFrontendError = (error: unknown, context?: FrontendErrorContext) => {
  // Keep this guard in sync with the Sentry.init guard in sentry.ts — when Sentry isn't
  // initialized (disabled or local) capturing is a no-op anyway, so bail early.
  if (!appInfo.useSentry || appInfo.sentryEnvironment === 'local') return

  Sentry.withScope((scope) => {
    scope.setTag('route', window.location.pathname)

    if (appInfo.gitCommitHash) scope.setTag('gitCommitHash', appInfo.gitCommitHash)

    scope.setContext('viewport', {
      width: window.innerWidth,
      height: window.innerHeight,
    })

    if (context?.tags) {
      for (const [k, v] of Object.entries(context.tags)) scope.setTag(k, v)
    }

    if (context?.extra) scope.setExtras(context.extra)

    if (error instanceof Error) {
      Sentry.captureException(error)
    } else if (typeof error === 'string') {
      Sentry.captureMessage(error, context?.level ?? 'error')
    } else {
      Sentry.captureException(new Error(`Non-Error captured: ${describeNonError(error)}`))
    }
  })
}
